import * as Tooltip from '@radix-ui/react-tooltip';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Plan } from '@/hooks/usePlan';
import { cn } from '@/lib/cn';

export interface LockBadgeProps {
  required: Exclude<Plan, 'free'>;
  size?: 'sm' | 'md';
  /** Optional className for the outer pill — lets callers tweak margins. */
  className?: string;
}

const tierStyle: Record<LockBadgeProps['required'], string> = {
  // Growth = brand accent (blue). Global = indigo (cooler / more enterprise-y).
  // Enterprise = amber (premium / scarce). Picked to read clearly on white
  // and on the ink-100/ink-50 page backgrounds.
  growth: 'border-accent-200 bg-accent-50 text-accent-700',
  global: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  enterprise: 'border-amber-200 bg-amber-50 text-amber-700',
};

const tierTooltipKey: Record<LockBadgeProps['required'], string> = {
  growth: 'plan.lock_growth_required',
  global: 'plan.lock_global_required',
  enterprise: 'plan.lock_enterprise_required',
};

const tierLabelKey: Record<LockBadgeProps['required'], string> = {
  growth: 'plan.tier_growth',
  global: 'plan.tier_global',
  enterprise: 'plan.tier_enterprise',
};

const sizeStyle = {
  sm: 'px-1.5 py-0.5 text-[10px] gap-0.5',
  md: 'px-2 py-0.5 text-[11px] gap-1',
};
const iconSize = { sm: 'h-2.5 w-2.5', md: 'h-3 w-3' };

/**
 * Compact pill rendered next to a feature label to signal it's gated by a
 * higher plan. Hover/focus shows a tooltip with the upgrade prompt.
 */
export function LockBadge({
  required,
  size = 'sm',
  className,
}: LockBadgeProps) {
  const { t } = useTranslation();
  const planLabel = t(tierLabelKey[required]);

  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span
            className={cn(
              'inline-flex items-center font-semibold uppercase tracking-wide rounded-md border',
              tierStyle[required],
              sizeStyle[size],
              className,
            )}
            aria-label={t(tierTooltipKey[required], { plan: planLabel })}
          >
            <Lock className={iconSize[size]} aria-hidden />
            {planLabel}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            sideOffset={4}
            className="z-[70] rounded-md bg-ink-900 px-2 py-1 text-[11px] font-medium text-white shadow-soft"
          >
            {t(tierTooltipKey[required], { plan: planLabel })}
            <Tooltip.Arrow className="fill-ink-900" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
